import { type ConnectorInfo } from '@blaze-shared/api-contracts-domain';
import { type BaseSchema, type ColumnQuery, isColumnId } from '@blaze-shared/schema-domain';
import { type Nullish } from '@blaze-shared/shared-util';
import { type DataAssignment, MAIN_INPUT_NAME } from '@blaze-shared/workflow-domain';

import {
  extractColumnsWithInputsFromString,
  extractNamedInputFromString,
  extractParamsFromString,
  extractTagsFromString,
} from './parse-string';
import {
  type WorkflowNodeColumnReference,
  type WorkflowNodeNamedInputReference,
  type WorkflowNodeParamReference,
  type WorkflowNodeTagReference,
} from './references.models';
import {
  findSchemaForApiParam,
  findSchemaIdForColumn,
  findSchemaIdForInput,
  findSchemaIdForTag,
} from './schema.helpers';

export function getTagReferencesFromString(
  inputString: Nullish<string>,
  schemas: Map<string | symbol, BaseSchema>,
  sourceId?: string,
): WorkflowNodeTagReference[] {
  if (!inputString) {
    return [];
  }

  const references: WorkflowNodeTagReference[] = [];
  const tags = extractTagsFromString(inputString);

  for (const tag of tags) {
    references.push({
      tag,
      schemaId: findSchemaIdForTag(tag, schemas),
      sourceId,
    });
  }

  return references;
}

export function getColumnReferencesFromNodeAssignments(
  node: { assignments?: DataAssignment[]; dialogAssignments?: DataAssignment[] },
  schemas: Map<string | symbol, BaseSchema>,
  baseSourceId: string,
): WorkflowNodeColumnReference[] {
  const columnnsInValues = (node.assignments ?? []).flatMap((assignment, i) => {
    const isDialogAssignment = node.dialogAssignments?.some((da) => da.name === assignment.name);
    return getColumnReferencesFromString(assignment.value, schemas, baseSourceId + i, {
      ignoreInputName: isDialogAssignment,
    });
  });
  const columnsInAssignmentNames = (node.assignments ?? []).flatMap((assignment, i) => {
    const isDialogAssignment = node.dialogAssignments?.some((da) => da.name === assignment.name);
    if (!isColumnId(assignment.name)) {
      return [];
    }
    return getColumnReferencesFromString(assignment.name, schemas, baseSourceId + i, {
      ignoreInputName: isDialogAssignment,
    });
  });
  return [...columnnsInValues, ...columnsInAssignmentNames];
}

export function getColumnReferencesFromInput1(
  columns: ColumnQuery[],
  schemas: Map<string | symbol, BaseSchema>,
  baseSourceId: string,
  indexedErrors = false,
): WorkflowNodeColumnReference[] {
  const input1Schema = schemas.get(MAIN_INPUT_NAME);
  return (columns ?? []).flatMap((column, i) => {
    const colUsedInNodeExistsInInput1 = !!input1Schema?.columns?.find(
      (col) => !!column && !!col && (col.id === column.id || col.name === column.name),
    );
    const sourceId = indexedErrors ? `${baseSourceId}${i}` : baseSourceId;
    return {
      columns: [column.id || column.name],
      inputName: '',
      schemaId: colUsedInNodeExistsInInput1 ? input1Schema?.id : undefined,
      sourceId,
    } as WorkflowNodeColumnReference;
  });
}

export function getColumnReferencesFromString(
  inputString: Nullish<string>,
  schemas: Map<string | symbol, BaseSchema>,
  sourceId?: string,
  options: {
    fallbackSourceIdToInputName?: boolean;
    ignoreInputName?: boolean;
  } = { fallbackSourceIdToInputName: false, ignoreInputName: false },
): WorkflowNodeColumnReference[] {
  if (!inputString) {
    return [];
  }

  const references = extractColumnsWithInputsFromString(inputString);
  return references.map((ref) => {
    if (ref.inputName && !options.ignoreInputName) {
      const schemaId = findSchemaIdForInput(ref.inputName, ref.columns, schemas);
      // JOIN NODE CORNER CASE
      // when the schemaId is not found (example: importcsvdata node), we fallback to the inputName
      if (options.fallbackSourceIdToInputName) {
        return {
          ...ref,
          sourceId: ref.inputName,
          schemaId,
        };
      }
      return {
        ...ref,
        sourceId,
        schemaId,
      };
    }

    return {
      ...ref,
      sourceId,
      schemaId: findSchemaIdForColumn(ref.columns[0], schemas),
    };
  });
}

export function getParamReferencesFromString(
  inputString: Nullish<string>,
  schemas: Map<string | symbol, BaseSchema>,
  connectors: ConnectorInfo[],
  sourceId?: string,
): WorkflowNodeParamReference[] {
  if (!inputString) {
    return [];
  }

  const references: WorkflowNodeParamReference[] = [];
  const params = extractParamsFromString(inputString);

  for (const param of params) {
    const reference: WorkflowNodeParamReference = {
      param,
      sourceId,
    };

    references.push(reference);

    const schema = findSchemaForApiParam(param, connectors);
    if (schema) {
      reference.api = {
        connectorId: schema.connectorId,
        actionName: schema.schemaName,
      };

      continue;
    }

    reference.schemaId = findSchemaIdForColumn(param, schemas);
  }

  return references;
}

export function getNamedInputReferencesFromString(
  inputString: Nullish<string>,
  schemas: Map<string | symbol, BaseSchema>,
  sourceId?: string,
): WorkflowNodeNamedInputReference[] {
  if (!inputString) {
    return [];
  }

  const references = extractNamedInputFromString(inputString).map((namedInput) => ({
    namedInput,
    schemaId: findSchemaIdForInput(namedInput, [], schemas),
    sourceId,
  }));

  return references;
}
